<template>
  <div class="pricing-grid">
    <article
      v-for="offer in offers"
      :key="offer.id"
      class="pricing-tile"
      :class="[
        offer.variant === 'primary'
          ? 'pricing-tile--primary'
          : 'pricing-tile--light',
      ]"
    >
      <header class="pricing-tile__header">
        <p v-if="offer.eyebrow || eyebrow" class="pricing-tile__eyebrow">
          {{ offer.eyebrow || eyebrow }}
        </p>
        <div
          class="pricing-tile__title-row"
          :class="{ 'pricing-tile__title-row--single': !offer.badge }"
        >
          <h3 class="pricing-tile__title">{{ offer.title }}</h3>
          <span
            v-if="offer.badge"
            class="pricing-tile__badge"
            :class="{ 'pricing-tile__badge--night': offer.badge === 'Soir' }"
          >
            {{ offer.badge }}
          </span>
        </div>
      </header>

      <div class="pricing-tile__price-box">
        <p class="pricing-tile__price">{{ offer.price }}<span>€</span></p>
        <p
          v-if="'fee' in offer"
          class="pricing-tile__fee"
          :class="{ 'pricing-tile__fee--placeholder': !offer.fee }"
        >
          {{ offer.fee || " " }}
        </p>
      </div>

      <ul class="pricing-tile__meta">
        <li v-for="detail in offer.meta" :key="`${offer.id}-${detail.label}`">
          <span class="pricing-tile__meta-label">{{ detail.label }}</span>
          <strong class="pricing-tile__meta-value">{{ detail.value }}</strong>
        </li>
      </ul>

      <div v-if="offer.note" class="pricing-tile__note-wrap">
        <div class="pricing-tile__note">
          <p class="pricing-tile__note-title">{{ offer.note.title }}</p>
          <p class="pricing-tile__note-copy">{{ offer.note.text }}</p>
        </div>
      </div>

      <ul
        v-if="offer.highlights && offer.highlights.length"
        class="pricing-tile__highlights"
      >
        <li
          v-for="highlight in offer.highlights"
          :key="`${offer.id}-${highlight}`"
        >
          {{ highlight }}
        </li>
      </ul>

      <footer class="pricing-tile__footer">
        <router-link :to="offer.route" class="pricing-tile__link">
          {{ offer.ctaLabel || "Détails »" }}
        </router-link>
      </footer>
    </article>
  </div>
</template>

<script>
export default {
  name: "ServicePricingGrid",
  props: {
    offers: {
      type: Array,
      required: true,
    },
    eyebrow: {
      type: String,
      default: "",
    },
  },
};
</script>

<style lang="scss" scoped>
.pricing-grid {
  width: min(100%, 1180px);
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(1, minmax(0, 1fr));
  gap: 22px;

  @media screen and (min-width: 768px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media screen and (min-width: 1200px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

.pricing-tile {
  width: 100%;
  min-height: 100%;
  padding: clamp(22px, 2vw, 30px);
  border-radius: 24px;
  display: flex;
  flex-direction: column;
  gap: 18px;
  background: #ffffff;
  box-shadow: 0 10px 24px rgba(0, 42, 84, 0.08);
}

.pricing-tile--primary {
  background: $color-primary;
  box-shadow: 0 14px 28px rgba(0, 42, 84, 0.16),
    inset 0 1px 0 rgba(245, 236, 236, 0.06);
}

.pricing-tile--light {
  background: $color-secondary;
  box-shadow: 0 12px 24px rgba(0, 42, 84, 0.12),
    inset 0 1px 0 rgba(255, 255, 255, 0.4);
}

.pricing-tile__header,
.pricing-tile__price-box,
.pricing-tile__meta,
.pricing-tile__note-wrap,
.pricing-tile__highlights,
.pricing-tile__footer {
  width: 100%;
}

.pricing-tile__header {
  min-height: 88px;
}

.pricing-tile__eyebrow {
  margin: 0 0 10px;
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgba(0, 42, 84, 0.48);
  text-align: left;
}

.pricing-tile__title-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: start;
  gap: 12px;
}

.pricing-tile__title-row--single {
  grid-template-columns: minmax(0, 1fr);
}

.pricing-tile__title {
  margin: 0;
  font-size: clamp(1.36rem, 1.14rem + 0.5vw, 1.7rem);
  line-height: 1.08;
  color: $color-primary;
  text-align: left;
}

.pricing-tile__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 34px;
  padding: 6px 12px;
  border-radius: 999px;
  background: $color-secondary;
  color: $color-primary;
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  box-shadow: 0 0 0 1px rgba(254, 185, 20, 0.24),
    0 0 16px rgba(254, 185, 20, 0.18);
}

.pricing-tile__badge--night {
  background: #000000;
  color: $color-secondary;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.24), 0 0 16px rgba(0, 0, 0, 0.18);
}

.pricing-tile__price-box {
  min-height: 90px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 16px 18px;
  border-radius: 18px;
}

.pricing-tile__price {
  margin: 0;
  font-family: "Spartan", "Arial Black", Arial, sans-serif;
  font-weight: 700;
  font-size: clamp(2.5rem, 2.1rem + 0.9vw, 3rem);
  line-height: 0.95;
  text-align: left;
  color: $color-primary;

  & span {
    color: $color-secondary;
    font-size: 0.42em;
  }
}

.pricing-tile__fee {
  margin: 8px 0 0;
  min-height: 1.42em;
  font-size: 0.9rem;
  line-height: 1.42;
  text-align: left;
  color: rgba(88, 89, 91, 0.88);
}

.pricing-tile__fee--placeholder {
  visibility: hidden;
}

.pricing-tile__meta {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 12px;

  & li {
    padding: 0;
    text-align: left;
  }
}

.pricing-tile__meta-label {
  display: block;
  margin-bottom: 4px;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(0, 42, 84, 0.5);
}

.pricing-tile__meta-value {
  display: block;
  font-size: 0.96rem;
  line-height: 1.35;
  color: $color-text;
}

.pricing-tile__note-wrap {
  flex: 1 1 auto;
  display: flex;
  align-items: center;
}

.pricing-tile__note {
  width: 100%;
  padding: 16px 18px;
  border-radius: 18px;
  background: rgba(0, 0, 0, 0.42);
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.14);
  backdrop-filter: blur(2px);
}

.pricing-tile__note-title {
  margin: 0 0 8px;
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  text-align: left;
  color: rgba(255, 255, 255, 0.92);
}

.pricing-tile__note-copy {
  margin: 0;
  font-size: 0.95rem;
  line-height: 1.45;
  text-align: left;
  color: $color-text-light;
}

.pricing-tile__highlights {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  gap: 12px;

  & li {
    position: relative;
    padding-left: 18px;
    font-size: 0.98rem;
    line-height: 1.42;
    text-align: left;
    color: $color-text;

    &::before {
      content: "";
      position: absolute;
      top: 0.62rem;
      left: 0;
      width: 7px;
      height: 7px;
      border-radius: 50%;
      background: $color-secondary;
    }
  }
}

.pricing-tile__footer {
  margin-top: auto;
  padding-top: 6px;
}

.pricing-tile__link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 54px;
  border-radius: 16px;
  padding: 0 18px;
  background: $color-secondary;
  color: $color-primary;
  font-size: 0.9rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  text-decoration: none;
  transition: transform 0.2s ease, background-color 0.2s ease, color 0.2s ease,
    box-shadow 0.2s ease;
  box-shadow: 0 0 0 1px rgba(254, 185, 20, 0.24),
    0 0 18px rgba(254, 185, 20, 0.2), 0 10px 18px rgba(0, 42, 84, 0.14);

  &:hover {
    transform: translateY(-1px);
    background: darken($color-secondary, 4%);
    color: $color-primary;
    text-decoration: none;
    box-shadow: 0 0 0 1px rgba(254, 185, 20, 0.28),
      0 0 22px rgba(254, 185, 20, 0.24), 0 12px 22px rgba(0, 42, 84, 0.16);
  }
}

.pricing-tile--primary {
  .pricing-tile__eyebrow {
    color: rgba(245, 236, 236, 0.72);
  }

  .pricing-tile__title {
    color: $color-text-light;
  }

  .pricing-tile__badge {
    background: $color-secondary;
    color: $color-primary;
  }

  .pricing-tile__badge--night {
    background: #000000;
    color: $color-secondary;
  }

  .pricing-tile__price-box {
    background: rgba(245, 236, 236, 0.04);
    box-shadow: inset 0 0 0 1px rgba(245, 236, 236, 0.08);
  }

  .pricing-tile__price {
    color: $color-text-light;
  }

  .pricing-tile__fee {
    color: rgba(245, 236, 236, 0.74);
  }

  .pricing-tile__meta-label {
    color: rgba(245, 236, 236, 0.62);
  }

  .pricing-tile__meta-value,
  .pricing-tile__highlights li {
    color: $color-text-light;
  }
}

.pricing-tile--light {
  .pricing-tile__eyebrow {
    color: rgba(0, 42, 84, 0.72);
  }

  .pricing-tile__badge {
    background: $color-primary;
    color: $color-secondary;
    box-shadow: 0 0 0 1px rgba(0, 42, 84, 0.14), 0 0 16px rgba(0, 42, 84, 0.14);
  }

  .pricing-tile__price-box {
    background: rgba(0, 0, 0, 0.36);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.18),
      inset 0 0 0 1px rgba(0, 0, 0, 0.22);
  }

  .pricing-tile__fee {
    color: rgba(255, 255, 255, 0.74);
  }

  .pricing-tile__title,
  .pricing-tile__meta-value,
  .pricing-tile__meta-label {
    color: #000000;
  }

  .pricing-tile__price {
    color: $color-text-light;
  }

  .pricing-tile__highlights li {
    color: $color-primary;
  }

  .pricing-tile__highlights li::before {
    background: $color-primary;
  }

  .pricing-tile__link {
    background: $color-primary;
    color: $color-secondary;
    box-shadow: 0 0 0 1px rgba(0, 42, 84, 0.16), 0 0 18px rgba(0, 42, 84, 0.16),
      0 10px 18px rgba(0, 42, 84, 0.14);
  }

  .pricing-tile__link:hover {
    background: lighten($color-primary, 4%);
    color: $color-secondary;
    box-shadow: 0 0 0 1px rgba(0, 42, 84, 0.18), 0 0 22px rgba(0, 42, 84, 0.18),
      0 12px 22px rgba(0, 42, 84, 0.16);
  }
}

@media screen and (max-width: 767px) {
  .pricing-grid {
    gap: 16px;
  }

  .pricing-tile {
    padding: 20px;
    border-radius: 22px;
    gap: 16px;
  }
}
</style>
